import { IPropWithClassName } from '@/types'
import { useSyllabusAzureSuggestion } from '@/utils/azure-search/useSyllabusAzureSearch'
import {
	useAppInsightsContext,
	useTrackEvent,
} from '@microsoft/applicationinsights-react-js'
import Close from '@mui/icons-material/Close'
import { ClickAwayListener, useAutocomplete } from '@mui/material'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { Button } from 'nsw-ds-react'
import { useEffect, useRef, useState } from 'react'
import Icon from './Icon'

export type AzureSearchBarAutocompleteProps = IPropWithClassName & {
	initialSearchText: string
	placeholder: string
	handleSearchPress: (_value: string) => void
	hideSearchButton?: boolean
	hideClearButton?: boolean
	searchInputName?: string
	inputClassName?: string
	autoFocus?: boolean

	/** Number of suggestions to show */
	suggestionNumber?: number

	disableNextRouteRedirect?: boolean
	clickingItemTrackEventName?: string
	searchingTrackEventName?: string
}

const isExternalUrl = (url: string) => {
	const currentUrl = new URL(window.location.href)
	const urlObj = new URL(url)
	return urlObj.host !== currentUrl.host
}

export function AzureSearchBarAutocomplete({
	initialSearchText = '',
	placeholder,
	handleSearchPress,
	hideSearchButton = false,
	hideClearButton = false,
	searchInputName = 'q',
	inputClassName,
	suggestionNumber = 10,
	disableNextRouteRedirect = false,
	autoFocus = false,
	clickingItemTrackEventName = 'Click Search Suggestion',
	searchingTrackEventName = 'Search',
}: AzureSearchBarAutocompleteProps) {
	const refIsFirstLoaded = useRef(true)
	const [q, setQ] = useState(initialSearchText)
	const [suggestionOpen, setSuggestionOpen] = useState(false)
	const [isInputDisabled, setIsInputDisabled] = useState(false)
	const route = useRouter()

	const { data, isFetched } = useSyllabusAzureSuggestion(q, suggestionNumber)
	const appInsights = useAppInsightsContext()
	const trackClick = useTrackEvent(
		appInsights,
		clickingItemTrackEventName,
		{
			url: '',
			title: '',
		},
		true,
	)
	const trackSearch = useTrackEvent(
		appInsights,
		searchingTrackEventName,
		{
			searchText: q,
		},
		true,
	)

	const {
		getRootProps,
		getInputProps,
		getListboxProps,
		getOptionProps,
		groupedOptions,
	} = useAutocomplete({
		inputValue: q,
		open: suggestionOpen,
		options: isFetched ? data?.json?.value || [] : [],
		freeSolo: true,
		autoHighlight: false,
		autoSelect: false,
		getOptionLabel: (option) => {
			const label =
				typeof option !== 'string' ? option?.title || '' : option
			return label
		},
		onInputChange: (_event, value) => {
			if (_event['key'] === 'Enter' && !value) {
				// don't set to empty if it's on enter
				return
			}
			setQ(value)
		},
		async onChange(_event, value) {
			try {
				_event.preventDefault()
				if (!value) return
				// Handle free solo string search
				if (typeof value === 'string') {
					trackSearch({
						searchText: value,
					})
					handleSearchPress(value)
					return
				}
				// Handle suggestion navigation
				const navigateToUrl = async (url: string) => {
					refIsFirstLoaded.current = true
					const isExternal = isExternalUrl(url)
					trackClick({
						url: url,
						title: value.title,
					})
					if (isExternal) {
						const newWindow = window.open(url, '_blank')
						if (newWindow) {
							newWindow.focus()
						}
					} else {
						window.location.href = url
					}
				}
				const { url } = value
				setSuggestionOpen(false)
				if (url.startsWith('http')) {
					await navigateToUrl(url)
					return
				}
				if (!disableNextRouteRedirect) {
					route.push(url)
				} else {
					await navigateToUrl(url)
				}
			} catch (error) {
				console.error('Error during search navigation:', error)
				// You may want to add error handling UI here
			}
		},
		filterOptions: (option) => option,
		clearOnBlur: false,
		selectOnFocus: false,
	})

	const handleClearSearch = () => {
		setQ('')
		handleSearchPress('')
	}

	const handlePressSearch = () => {
		trackSearch({
			searchText: q,
		})
		handleSearchPress(q)
	}

	const rootProps = getRootProps()

	useEffect(() => {
		setQ(initialSearchText)
	}, [initialSearchText])

	useEffect(() => {
		if (q && isFetched && !refIsFirstLoaded.current) {
			setSuggestionOpen(true)
		}
		if (isFetched) {
			refIsFirstLoaded.current = false
		}
	}, [q, isFetched])

	return (
		<div className="nsw-form__group relative !mt-0">
			<div
				className="nsw-form__input-group nsw-form__input-group--icon"
				{...rootProps}
			>
				<input
					type="text"
					// @ts-ignore
					enterkeyhint="Search"
					className={clsx('nsw-form__input !pr-12', inputClassName)}
					name={searchInputName}
					{...getInputProps()}
					value={q}
					placeholder={placeholder}
					disabled={isInputDisabled}
					autoFocus={autoFocus}
					onKeyDown={(e) => {
						if (['Escape', 'Enter'].includes(e.key)) {
							setSuggestionOpen(false)

							// @ts-ignore
							if (e.key === 'Enter' && e.target.value === '') {
								handleClearSearch()
							}
						}
						// Handle backspace when text is selected
						if (
							e.key === 'Backspace' &&
							window.getSelection()?.toString()
						) {
							const newValue = q.replace(
								window.getSelection()?.toString() || '',
								'',
							)
							setQ(newValue)
							e.preventDefault()
						}
					}}
				/>
				{q && !hideClearButton && (
					<button
						className="absolute right-[4rem] top-1/2 -translate-y-1/2 font-bold"
						type="button"
						onClick={handleClearSearch}
						aria-label="Clear search"
						disabled={isInputDisabled}
					>
						<span className="sr-only">Clear search</span>
						<Close />
					</button>
				)}
				{!hideSearchButton && (
					<Button
						type="button"
						className="nsw-button--flex"
						style="white"
						onClick={handlePressSearch}
						disabled={isInputDisabled}
					>
						<Icon icon="mdi:search" />
					</Button>
				)}
			</div>
			<ClickAwayListener onClickAway={() => setSuggestionOpen(false)}>
				<ul
					{...getListboxProps()}
					className="absolute z-20 left-0 w-full mt-1 bg-white rounded-[4px] border-nsw-grey-03 shadow-[0px_4px_12px_0px_rgba(0,0,0,.15)]"
				>
					{groupedOptions.map((option, index) => {
						return (
							<li
								{...getOptionProps({ option, index })}
								key={option['content_id']}
								dangerouslySetInnerHTML={{
									__html: option['@search.text'],
								}}
								className={clsx([
									'py-2 px-4 !mt-0 underline [&.Mui-focused]:bg-nsw-off-white cursor-pointer',
								])}
							></li>
						)
					})}
				</ul>
			</ClickAwayListener>
		</div>
	)
}
